// Is this phone ready for offline use (REQ-UX-06, BD-56, BD-71)? It is when the app itself is installed
// for offline use (its service worker controls the page, so the app opens without signal) and places,
// open invoices and animals have each been downloaded at least once; each shows when it was ready.
// A phone opened for the first time is therefore never ready before its service worker takes control.
import { useEffect, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "./db";

const READINESS_GROUPS = [
  { key: "places", label: "Places", tables: ["v_place_geo"] },
  { key: "invoices", label: "Open invoices", tables: ["v_invoice_summary", "v_item_state", "invoice_line"] },
  { key: "animals", label: "Animals", tables: ["v_animal_list"] }
] as const;

export type Readiness = { ready: boolean; groups: { key: string; label: string; at: string | null }[] };

const APP_AT = "app_offline_at";

/** Whether the service worker controls this page: only then does the app open without signal. */
function appControlled(): boolean {
  return typeof navigator !== "undefined" && !!navigator.serviceWorker?.controller;
}

async function offlineReadiness(controlled = appControlled()): Promise<Readiness> {
  const at = new Map((await db.meta.where("key").startsWith("cache_at:").toArray()).map((m) => [m.key.slice(9), String(m.value)]));
  const appAt = controlled ? String((await db.meta.get(APP_AT))?.value ?? new Date().toISOString()) : null;
  const groups = [
    { key: "app", label: "The app itself", at: appAt },
    ...READINESS_GROUPS.map((g) => {
      const times = g.tables.map((t) => at.get(t) ?? null);
      // A group counts from its oldest table: all of it must have been downloaded.
      return { key: g.key, label: g.label, at: times.includes(null) ? null : times.sort()[0]! };
    })
  ];
  return { ready: groups.every((g) => g.at), groups };
}

export function useOfflineReadiness(): Readiness | undefined {
  const [controlled, setControlled] = useState(appControlled);
  useEffect(() => {
    const sw = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
    if (!sw) return;
    const update = () => {
      const on = appControlled();
      setControlled(on);
      // Remember when the app first worked offline on this phone, for the details list.
      if (on) void db.meta.get(APP_AT).then(async (m) => { if (!m) await db.meta.put({ key: APP_AT, value: new Date().toISOString() }); });
    };
    update();
    sw.addEventListener("controllerchange", update);
    return () => sw.removeEventListener("controllerchange", update);
  }, []);
  return useLiveQuery(() => offlineReadiness(controlled), [controlled]);
}
